import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import RelatedPages from '@/components/base/RelatedPages';

const steps = [
  {
    num: '01',
    icon: 'ri-discuss-line',
    title: '需求諮詢',
    desc: '與業務專員進行初步溝通，說明您的品牌定位、目標族群、預計銷售通路與預算範圍，雙方確認合作方向。',
    duration: '1-3 天',
    color: '#4CAF50',
  },
  {
    num: '02',
    icon: 'ri-flask-line',
    title: '配方提案',
    desc: '研發團隊依需求提出配方建議，說明原料功效、成分比例與初步報價，客戶確認後進行試製樣品。',
    duration: '2-3 週',
    color: '#43a047',
  },
  {
    num: '03',
    icon: 'ri-palette-line',
    title: '包裝設計',
    desc: '提供包裝規格建議，協助客戶確認容器選型、標示版面配置，並審查包裝文字符合食品標示法規。',
    duration: '1-2 週',
    color: '#388e3c',
  },
  {
    num: '04',
    icon: 'ri-verified-badge-line',
    title: '檢驗認證',
    desc: '樣品送第三方機構進行安全性檢驗，出具微生物、重金屬、農藥殘留等完整 CoA 檢驗報告，確保合規上市。',
    duration: '2-4 週',
    color: '#2e7d32',
  },
  {
    num: '05',
    icon: 'ri-settings-3-line',
    title: '量產確認',
    desc: '簽訂合約、確認訂單數量後正式進入量產，生產過程皆有品管人員把關，生產週期依實際訂單量調整。',
    duration: '4–8 週',
    color: '#1b5e20',
  },
  {
    num: '06',
    icon: 'ri-truck-line',
    title: '完成交貨',
    desc: '成品通過品管驗收後按約定時程出貨，提供完整出貨文件，後續持續追蹤並提供技術支援。',
    duration: '依合約約定',
    color: '#4CAF50',
  },
];

const highlights = [
  { icon: 'ri-user-line', label: '專屬業務窗口，全程陪伴' },
  { icon: 'ri-shield-check-line', label: 'NDA 保密協議保障您的配方' },
  { icon: 'ri-time-line', label: '流程清晰，降低製程焦慮' },
  { icon: 'ri-file-check-line', label: '完整文件支援，合規有保障' },
];

export default function ProcessContent() {
  return (
    <>
      {/* Hero Banner */}
      <section className="relative pt-20 min-h-[360px] md:min-h-[420px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=professional%20business%20team%20planning%20project%20roadmap%20on%20whiteboard%20in%20modern%20bright%20office%2C%20health%20supplement%20brand%20collaboration%20meeting%2C%20clean%20minimal%20workspace%20with%20green%20plants%2C%20natural%20daylight&width=1920&height=420&seq=proc-hero-bg-01&orientation=landscape"
            alt="合作流程背景"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a3a1a]/90 via-[#1a3a1a]/65 to-[#1a3a1a]/20" />
        </div>
        <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-28">
          <div className="max-w-2xl">
            <div className="mb-5">
              <div className="inline-block bg-black/8 backdrop-blur-sm rounded-lg px-3 py-1.5" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>
                <Breadcrumb
                  items={[
                    { label: '首頁', href: '/' },
                    { label: '合作相關', href: '/cooperation' },
                    { label: '合作流程' },
                  ]}
                  textClassName="text-white"
                />
              </div>
            </div>
            <span className="inline-block bg-[#4CAF50]/30 border border-[#4CAF50]/40 text-white text-[0.875rem] font-semibold px-4 py-1.5 rounded-full mb-5 shadow-[0_1px_3px_rgba(0,0,0,0.2)]">
              合作相關
            </span>
            <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">
              合作流程
            </h1>
            <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl">
              從需求對焦到完成交貨，以清楚可控的流程協助品牌完成產品開發。
            </p>
          </div>
        </div>
      </section>

      {/* Highlights bar */}
      <section className="bg-white border-b border-gray-100 py-5">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {highlights.map((h) => (
              <div key={h.label} className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-green-50 rounded-lg flex-shrink-0 border border-green-100">
                  <i className={`${h.icon} text-[#4CAF50] text-base`} />
                </div>
                <span className="text-[#1a2e1a] text-[0.875rem] font-medium leading-snug">{h.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Steps — Desktop horizontal / Mobile vertical */}
      <section className="py-20 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              六步驟流程
            </span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">
              從諮詢到交貨，步步清晰
            </h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              每個步驟均有專業人員陪伴，讓您在整個合作過程中安心、放心
            </p>
          </div>

          {/* Desktop flow */}
          <div className="hidden lg:block">
            {/* Row 1: steps 1–3 */}
            <div className="grid grid-cols-3 gap-0 mb-0">
              {steps.slice(0, 3).map((s, idx) => (
                <div key={s.num} className="relative flex flex-col items-center">
                  {/* Connector line right */}
                  {idx < 2 && (
                    <div className="absolute top-10 left-1/2 w-full h-0.5 bg-green-200 z-0" />
                  )}
                  <div className="relative z-10 flex flex-col items-center px-5 w-full">
                    <div className="w-20 h-20 flex items-center justify-center rounded-full border-2 border-[#4CAF50] bg-white mb-4 relative">
                      <i className={`${s.icon} text-2xl text-[#4CAF50]`} />
                      <span className="absolute -top-3.5 -right-3.5 w-9 h-9 flex items-center justify-center bg-[#4CAF50] text-white text-[0.875rem] font-bold rounded-full">
                        {s.num}
                      </span>
                    </div>
                    <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-2 text-center">{s.title}</h3>
                    <p className="text-gray-500 text-[0.875rem] leading-[1.75] text-center mb-3">{s.desc}</p>
                    <span className="inline-flex items-center gap-1.5 text-[#4CAF50] text-[0.8125rem] font-medium">
                      <i className="ri-time-line text-sm" />
                      {s.duration}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Down arrow connector — step 3 → step 4 */}
            <div className="flex justify-end pr-[16.67%] my-3">
              <div className="flex items-center justify-center w-11 h-11 rounded-full bg-green-100 border-2 border-green-200 shadow-[0_1px_8px_rgba(76,175,80,0.3)]">
                <i className="ri-arrow-down-line text-[#4CAF50] text-xl" />
              </div>
            </div>

            {/* Row 2: steps 4–6 reversed layout */}
            <div className="grid grid-cols-3 gap-0">
              {steps.slice(3, 6).reverse().map((s, idx) => (
                <div key={s.num} className="relative flex flex-col items-center">
                  {idx < 2 && (
                    <div className="absolute top-10 left-1/2 w-full h-0.5 bg-green-200 z-0" />
                  )}
                  <div className="relative z-10 flex flex-col items-center px-5 w-full">
                    <div className="w-20 h-20 flex items-center justify-center rounded-full border-2 border-[#4CAF50] bg-white mb-4 relative">
                      <i className={`${s.icon} text-2xl text-[#4CAF50]`} />
                      <span className="absolute -top-3.5 -right-3.5 w-9 h-9 flex items-center justify-center bg-[#4CAF50] text-white text-[0.875rem] font-bold rounded-full">
                        {s.num}
                      </span>
                    </div>
                    <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-2 text-center">{s.title}</h3>
                    <p className="text-gray-500 text-[0.875rem] leading-[1.75] text-center mb-3">{s.desc}</p>
                    <span className="inline-flex items-center gap-1.5 text-[#4CAF50] text-[0.8125rem] font-medium">
                      <i className="ri-time-line text-sm" />
                      {s.duration}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile vertical flow */}
          <div className="lg:hidden space-y-0">
            {steps.map((s, idx) => (
              <div key={s.num} className="flex gap-4">
                {/* Left: icon + line */}
                <div className="flex flex-col items-center flex-shrink-0">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full border-2 border-[#4CAF50] bg-white relative flex-shrink-0">
                    <i className={`${s.icon} text-xl text-[#4CAF50]`} />
                    <span className="absolute -top-3 -right-3 w-8 h-8 flex items-center justify-center bg-[#4CAF50] text-white text-[0.8125rem] font-bold rounded-full">
                      {s.num}
                    </span>
                  </div>
                  {idx < steps.length - 1 && (
                    <div className="w-0.5 flex-1 bg-green-200 my-2 min-h-[40px]" />
                  )}
                </div>
                {/* Right: content */}
                <div className="pb-8 flex-1 min-w-0">
                  <h3 className="text-[#1a2e1a] font-bold text-[1rem] mb-1.5">{s.title}</h3>
                  <p className="text-gray-500 text-[0.875rem] leading-[1.75] mb-2">{s.desc}</p>
                  <span className="inline-flex items-center gap-1.5 text-[#4CAF50] text-[0.8125rem] font-medium">
                    <i className="ri-time-line text-sm" />
                    {s.duration}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 六大核心優勢 */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              六大核心優勢
            </span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">六大核心優勢</h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto">
              我們以專業技術、嚴格品管和完善服務，成為眾多品牌信賴的代工夥伴
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: 'ri-flask-line', title: '專業研發團隊', desc: '專業團隊以自然為根本，堅持自然選材，且持續追蹤最新原料趨勢與配方技術' },
              { icon: 'ri-shield-check-line', title: '品質認證保證', desc: '生產工廠擁有 HACCP、ISO22000 等國際品質認證' },
              { icon: 'ri-timer-line', title: '快速交期', desc: '從樣品確認到大量生產，幫助您有效縮短產品上市時程' },
              { icon: 'ri-settings-3-line', title: '彈性客製化', desc: '針對客戶需求做彈性的客製化製程，幫助您輕鬆打造產品' },
              { icon: 'ri-auction-line', title: '注重法規細節', desc: '為您把關食品相關法規，打造低風險產品' },
              { icon: 'ri-file-list-3-line', title: '協助檢驗送審', desc: '協助客戶完成產品標示，並協助送審至第三方檢驗單位' },
            ].map((item) => (
              <div key={item.title} className="bg-[#f7faf7] rounded-2xl p-6 border border-gray-100 text-left hover:border-green-200 hover:-translate-y-1 transition-all duration-300">
                <div className="w-11 h-11 flex items-center justify-center bg-white rounded-xl border border-green-100 mb-4">
                  <i className={`${item.icon} text-[#4CAF50] text-xl`} />
                </div>
                <h4 className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-2">{item.title}</h4>
                <p className="text-gray-500 text-[0.9375rem] leading-[1.75]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] leading-tight mb-4">
            準備好開始了嗎？
          </h2>
          <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto mb-8">
            只需一通電話或一封訊息，就能啟動第一步。康永業務團隊一個工作天內回覆。
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact/form"
              className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer"
            >
              開始合作諮詢
              <i className="ri-arrow-right-line" />
            </Link>
            <Link
              to="/cooperation/faq"
              className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer"
            >
              查看常見問題
            </Link>
          </div>
        </div>
      </section>
      <RelatedPages items={[
        { title: '其他服務', desc: '法規風險、行銃文案、進口原料代購…了解康永的全方位合作升値服務。', href: '/cooperation/services' },
        { title: '常見問題', desc: '品牌、起訂量、法規、通路…常見答疑全整理。', href: '/cooperation/faq' },
        { title: '立即洽詢', desc: '填寫諮詢表單，啟動合作第一步。', href: '/contact/form' },
      ]} />
    </>
  );
}
